
import { Option } from '@/types/apptypes';
import {
	CqlAttributeGroupData,
	CqlAttributeData,
	CqlGroupEntry,
	CqlQueryBuilderOptions,
	isCqlAttributeData,
	isCqlAttributeGroupData,
	CqlAnnotationCombinator,
} from '@/components/cql/cql-types';
import CqlAttribute from './CqlAttribute.vue';
import CqlAddAttributeButton from './CqlAddAttributeButton.vue';
import uid from '@/mixins/uid';

import useModel from './useModel';

export default useModel<CqlAttributeGroupData>().extend({
	name: 'CqlAttributeGroup',
	components: {
		CqlAttribute,
		CqlAddAttributeButton
	},
	props: {
		isRoot: { type: Boolean, default: false },
		options: { type: Object as () => CqlQueryBuilderOptions, required: true },
	},
	computed: {
		shouldShowAddControls(): boolean {
			// Show add controls if we have any content or if we're the root group
			return this.isRoot || this.model.entries.length > 0;
		},
		currentOperatorOption(): Option {
			return this.options.operatorOptions.find(op => op.value === this.model.operator) || {
				value: this.model.operator,
				label: this.model.operator
			}
		}
	},
	methods: {
		// Helper methods for type checking
		isCqlAttributeData(entry: CqlGroupEntry): entry is CqlAttributeData {
			return 'annotationId' in entry;
		},

		isCqlAttributeGroupData(entry: CqlGroupEntry): entry is CqlAttributeGroupData {
			return 'entries' in entry;
		},

		createDefaultAttribute(): CqlAttributeData {
			return {
				id: `attr_${uid()}`,
				annotationId: this.options.defaultAnnotationId,
				comparator: '=',
				values: [''],
				caseSensitive: false
			};
		},

		addAttribute(operator: CqlAnnotationCombinator, calledForAttribute?: CqlAttributeData) {
			// Optimization: If there's only one attribute in the group,
			// just change the operator and add a new attribute instead of creating nested groups
			if (this.model.entries.length <= 1) {
				// Change the group's operator to the new operator
				this.model.operator = operator;
			}
			if (operator === this.model.operator) {
				// just insert a new attribute
				const index = calledForAttribute
					? this.model.entries.findIndex(e => e.id === calledForAttribute.id) + 1
					: this.model.entries.length;
				this.model.entries.splice(index, 0, this.createDefaultAttribute());
				this.emitUpdate();
				return;
			}

			// Replace existing attribute with a group containing it + a new attribute
			if (calledForAttribute) {
				const newGroup: CqlAttributeGroupData = {
					id: `group_${uid()}`,
					operator: operator,
					entries: [calledForAttribute, this.createDefaultAttribute()]
				};
				const index = this.model.entries.findIndex((entry: CqlGroupEntry) => entry.id === calledForAttribute.id);
				this.model.entries.splice(index, 1, newGroup);
				this.emitUpdate();
				return;
			}

			// Base case, replace whole group.
			const newGroup = this.model;
			this.model = {
				id: `group_${uid()}`,
				operator: operator,
				entries: [newGroup, this.createDefaultAttribute()]
			};
			this.emitUpdate();
		},

		updateAttribute(updatedAttribute: CqlAttributeData) {
			const index = this.model.entries.findIndex((entry: CqlGroupEntry) =>
				this.isCqlAttributeData(entry) && entry.id === updatedAttribute.id);
			if (index !== -1) {
				this.$set(this.model.entries, index, updatedAttribute);
				this.emitUpdate();
			}
		},

		deleteAttribute(attributeId: string) {
			const index = this.model.entries.findIndex((entry: CqlGroupEntry) =>
				this.isCqlAttributeData(entry) && entry.id === attributeId);
			if (index !== -1) {
				this.model.entries.splice(index, 1);
				this.checkIfShouldRemoveSingleEntryGroup();
				this.emitUpdate();
			}
		},

		updateNestedGroup(updatedGroup: CqlAttributeGroupData) {
			const index = this.model.entries.findIndex((entry: CqlGroupEntry) =>
				this.isCqlAttributeGroupData(entry) && entry.id === updatedGroup.id);
			if (index !== -1) {
				this.$set(this.model.entries, index, updatedGroup);
				this.emitUpdate();
			}
		},

		deleteNestedGroup(groupId: string, replaceWith?: CqlGroupEntry) {
			const index = this.model.entries.findIndex((entry: CqlGroupEntry) =>
				this.isCqlAttributeGroupData(entry) && entry.id === groupId);
			if (index !== -1) {
				if (replaceWith) {
					// Replace the group with its remaining entry
					this.model.entries.splice(index, 1, replaceWith);
				} else {
					this.model.entries.splice(index, 1);
				}
				this.checkIfShouldRemoveSingleEntryGroup();
				this.emitUpdate();
			}
		},

		checkIfShouldRemoveSingleEntryGroup() {
			// If this is not the root group and only one entry remains (or none),
			// dissolve this group and move the remaining content (if any) to the parent
			if (!this.isRoot && this.model.entries.length <= 1) {
				this.$emit('delete-group', this.model.id, this.model.entries[0]);
			}
		},

		emitUpdate() {
			this.$emit('update:group', this.model);
		}
	},
});
